import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import chartOfAccountsAPI from "../api/chartOfAccountsAPI";
import accountTypesAPI from "../api/accountTypesAPI";

const AddAccountModal = ({ isOpen, onClose, onCreated, initialName = "", clientId = null }) => {
  const [loading, setLoading] = useState(false);
  const [accountTypes, setAccountTypes] = useState([]);
  const [parentAccounts, setParentAccounts] = useState([]);
  const [previewNumber, setPreviewNumber] = useState("");
  const [form, setForm] = useState({
    accountName: initialName,
    accountType: "",
    description: "",
    normalBalance: "debit",
  });

  // Keep a ref so the open effect can read current accountType without stale closure
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => {
    if (!isOpen) return;
    loadAccountTypes();
    loadParentAccounts();
    setForm(prev => ({ ...prev, accountName: initialName }));
    // Clear the stale preview number so checkDuplicateAccountCode() won't block the next save.
    // If a type was already selected, re-fetch the next available number for that type.
    const savedType = formRef.current.accountType;
    if (savedType) {
      chartOfAccountsAPI.getNextNumber(savedType)
        .then((next) => {
          const number = typeof next === 'string' ? next : (next?.nextNumber || '');
          setPreviewNumber(number);
        })
        .catch(() => setPreviewNumber(''));
    } else {
      setPreviewNumber('');
    }
  }, [isOpen, initialName]);

  const loadAccountTypes = async () => {
    try {
      const res = await accountTypesAPI.getAll({ activeOnly: true });
      const data = Array.isArray(res) ? res : res?.data || [];
      setAccountTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const getClientOptions = () =>
    clientId ? { headers: { 'x-accounting-client-id': clientId } } : {};

  const loadParentAccounts = async () => {
    try {
      const res = await chartOfAccountsAPI.getAll(
        undefined, undefined,
        { activeOnly: 'true', limit: 1000 },
        getClientOptions()
      );
      const data = res.data || res;
      setParentAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load parent accounts:", err);
    }
  };

  // ✅ Frontend validation: check for duplicate account name (case-insensitive)
  const checkDuplicateAccountName = () => {
    const trimmedName = form.accountName?.trim().toLowerCase();
    if (!trimmedName) return null;
    
    const duplicate = parentAccounts.find(
      (account) => account.accountName?.trim().toLowerCase() === trimmedName
    );
    
    return duplicate ? duplicate.accountName : null;
  };

  // ✅ Frontend validation: check for duplicate account code
  const checkDuplicateAccountCode = () => {
    if (!form.accountType) return null;
    
    try {
      const previewNum = previewNumber?.trim();
      if (!previewNum) return null;
      
      const duplicate = parentAccounts.find(
        (account) => account.accountNumber === previewNum
      );
      
      return duplicate ? duplicate.accountNumber : null;
    } catch (err) {
      return null;
    }
  };

const handleTypeChange = async (e) => {
  const accountTypeId = e.target.value;
  const type = accountTypes.find(t => t._id === accountTypeId);

  setForm(prev => ({
    ...prev,
    accountType: accountTypeId,
    normalBalance: type?.normalBalance || "debit",
  }));

  if (!accountTypeId) {
    setPreviewNumber("");
    return;
  }

  try {
    const next = await chartOfAccountsAPI.getNextNumber(accountTypeId);

    const number =
      typeof next === "string"
        ? next
        : next?.nextNumber || next?.data?.nextNumber || "";

    setPreviewNumber(number);
  } catch (err) {
    console.error(err);
  }
};


  const handleSubmit = async () => {
    if (!form.accountName || !form.accountType) return;

    // ✅ Check for duplicate account name (case-insensitive)
    const duplicateName = checkDuplicateAccountName();
    if (duplicateName) {
      alert(`An account named "${duplicateName}" already exists. Account names must be unique (case-insensitive).`);
      return;
    }

    // ✅ Check for duplicate account code
    const duplicateCode = checkDuplicateAccountCode();
    if (duplicateCode) {
      alert(`Account number "${duplicateCode}" already exists. Account codes must be unique.`);
      return;
    }

    try {
      setLoading(true);

      const res = await chartOfAccountsAPI.create(
        { ...form, accountNumber: "" },
        getClientOptions()
      );

      const newAccount = res.data || res;

      onCreated?.(newAccount);
      onClose();
    } catch (err) {
      alert(err.message || "Failed to create account");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-[550px] rounded-lg shadow-lg p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">Create New Account</h2>

        <div>
          <label className="text-sm font-medium">Account Name </label>
          <input
            value={form.accountName}
            onChange={(e) => setForm({ ...form, accountName: e.target.value })}
            className={`w-3/4 border rounded px-3 py-2 mt-1 ${
              checkDuplicateAccountName() ? "border-red-500 bg-red-50" : "border-gray-300"
            }`}
          />
          {checkDuplicateAccountName() && (
            <p className="text-xs text-red-600 mt-1 font-semibold">
              ✕ An account with this name already exists (case-insensitive)
            </p>
          )}
        </div>

        <div>
          <label className="text-sm font-medium">Account Type </label>
          <select
            value={form.accountType}
            onChange={handleTypeChange}
            className="w-3/4 border rounded px-3 py-2 mt-1"
          >
            <option value="">Select type</option>
            {accountTypes.map(t => (
              <option key={t._id} value={t._id}>
                {t.name} ({t.code})
              </option>
            ))}
          </select>
        </div>

        {previewNumber && (
          <div className={`text-sm ${checkDuplicateAccountCode() ? "text-red-600" : "text-gray-500"}`}>
            Account Number will be: <b>{previewNumber}</b>
            {checkDuplicateAccountCode() && (
              <p className="text-xs text-red-600 mt-1 font-semibold">
                ✕ Account number "{checkDuplicateAccountCode()}" already exists
              </p>
            )}
          </div>
        )}

        <div>
          <label className="text-sm font-medium">Description </label>
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-3/4 border rounded px-3 py-2 mt-1"
          />
        </div>

        <div className="flex justify-end gap-3 pt-3">
          <button type="button" onClick={onClose} className="px-3 py-2 border rounded">
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || checkDuplicateAccountName() || checkDuplicateAccountCode()}
            className="px-4 py-2 bg-teal-600 text-white rounded disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? "Creating..." : "Create Account"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddAccountModal;
